import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PerformanceInsights } from '../types';
import { TrendingUp, AlertTriangle, Target, Award } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';

interface PredictiveIntelligenceTabProps {
  insights: PerformanceInsights;
}

const PredictiveIntelligenceTab = ({ insights }: PredictiveIntelligenceTabProps) => {
  const predictiveData = insights.predictiveTalentAnalytics;

  if (!predictiveData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No predictive intelligence data available</p>
        </div>
      </div>
    );
  }

  // Mock data for charts when real data is not available
  const performanceForecastData = [
    { period: 'Q1', predicted: 3.2, actual: 3.1, confidence: 85 },
    { period: 'Q2', predicted: 3.4, actual: 3.3, confidence: 88 },
    { period: 'Q3', predicted: 3.6, actual: null, confidence: 82 },
    { period: 'Q4', predicted: 3.8, actual: null, confidence: 75 }
  ];

  // Enhanced goal velocity data with goal types
  const goalVelocityData = [
    { category: 'Financial', velocity: 85, trend: 'up' },
    { category: 'Project', velocity: 72, trend: 'stable' },
    { category: 'Customer', velocity: 90, trend: 'up' },
    { category: 'Innovation', velocity: 68, trend: 'down' },
    { category: 'Leadership', velocity: 78, trend: 'up' }
  ];

  // Mock team members with their specific goal types
  const teamMembersWithGoals = [
    { employee: 'Sarah Chen', velocityScore: 88, projectedCompletion: 'Q3 2024', goalType: 'Financial' },
    { employee: 'Michael Torres', velocityScore: 72, projectedCompletion: 'Q4 2024', goalType: 'Project' },
    { employee: 'Lisa Wang', velocityScore: 91, projectedCompletion: 'Q2 2024', goalType: 'Customer' },
    { employee: 'James Miller', velocityScore: 65, projectedCompletion: 'Q1 2025', goalType: 'Innovation' },
    { employee: 'Emily Rodriguez', velocityScore: 79, projectedCompletion: 'Q3 2024', goalType: 'Leadership' },
    { employee: 'David Kim', velocityScore: 83, projectedCompletion: 'Q4 2024', goalType: 'Financial' },
  ];

  // Helper function to convert riskLevel to a numeric score for display
  const getRiskScore = (riskLevel: 'Low' | 'Medium' | 'High') => {
    switch (riskLevel) {
      case 'High': return 85;
      case 'Medium': return 60;
      case 'Low': return 25;
      default: return 50;
    }
  };

  return (
    <div className="space-y-6">
      {/* Talent Pipeline Health Metrics - Top Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-secondary/30 rounded-lg p-4 text-center border border-cyan-500/20">
          <div className={`text-3xl font-bold mb-2 ${
            (predictiveData.talentPipelineHealth?.overallScore || 75) > 80 ? 'text-green-400' :
            (predictiveData.talentPipelineHealth?.overallScore || 75) > 60 ? 'text-yellow-400' : 'text-red-400'
          }`}>
            {predictiveData.talentPipelineHealth?.overallScore?.toFixed(0) || 75}%
          </div>
          <p className="text-gray-300 text-sm">Overall Health</p>
        </div>
        
        <div className="bg-secondary/30 rounded-lg p-4 text-center border border-blue-500/20">
          <div className="text-3xl font-bold text-blue-400 mb-2">12</div>
          <p className="text-gray-300 text-sm">Ready Successors</p>
        </div>
        
        <div className="bg-secondary/30 rounded-lg p-4 text-center border border-green-500/20">
          <div className="text-3xl font-bold text-green-400 mb-2">8</div>
          <p className="text-gray-300 text-sm">Emerging Talent</p>
        </div>
        
        <div className="bg-secondary/30 rounded-lg p-4 text-center border border-orange-500/20">
          <div className="text-3xl font-bold text-orange-400 mb-2">3</div>
          <p className="text-gray-300 text-sm">Critical Gaps</p>
        </div>
      </div>

      {/* 2x2 Grid for main sections with equal heights and scrollbars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Row 1, Column 1: Performance Forecasting */}
        <Card className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-blue-500/30 h-[500px] flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="text-white flex items-center gap-2">
              Performance Forecasting
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="h-48 mb-4 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={performanceForecastData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" opacity={0.6} />
                  <XAxis dataKey="period" stroke="#888" />
                  <YAxis domain={[0, 5]} stroke="#888" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #444',
                      borderRadius: '8px',
                      color: '#ffffff'
                    }}
                  />
                  <Line type="monotone" dataKey="predicted" stroke="#3b82f6" strokeWidth={3} name="Predicted" />
                  <Line type="monotone" dataKey="actual" stroke="#22c55e" strokeWidth={3} name="Actual" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="space-y-2">
                {predictiveData.performanceForecasting?.map((forecast, index) => (
                  <div key={index} className="bg-secondary/30 rounded-lg p-3 border border-blue-500/20">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">{forecast.employee}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-300">
                          {forecast.currentBand} → {forecast.predictedBand}
                        </span>
                        <span className="text-gray-400 text-sm">
                          {forecast.confidence}% confidence
                        </span>
                      </div>
                    </div>
                  </div>
                )) || (
                  <div className="text-center py-4 text-gray-400">
                    <p>No forecasting data available</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Row 1, Column 2: Flight Risk Analysis */}
        <Card className="bg-gradient-to-r from-red-900/20 to-pink-900/20 border-red-500/30 h-[500px] flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="text-white flex items-center gap-2">
              Flight Risk Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4">
              {predictiveData.flightRiskAnalysis?.map((risk, index) => {
                const riskScore = getRiskScore(risk.riskLevel);
                return (
                  <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-red-500/20">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-white font-semibold">{risk.employee}</h4>
                        <p className="text-gray-300 text-sm">
                          Risk Level: <span className={`font-medium ${
                            risk.riskLevel === 'High' ? 'text-red-400' :
                            risk.riskLevel === 'Medium' ? 'text-yellow-400' : 'text-green-400'
                          }`}>
                            {risk.riskLevel}
                          </span>
                        </p>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          riskScore > 75 ? 'text-red-400' :
                          riskScore > 50 ? 'text-yellow-400' : 'text-green-400'
                        }`}>
                          {riskScore.toFixed(0)}%
                        </div>
                        <p className="text-gray-400 text-sm">Risk Score</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            riskScore > 75 ? 'bg-red-500' :
                            riskScore > 50 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${riskScore}%` }}
                        />
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-300 text-sm mb-1">Key Indicators:</p>
                      <div className="flex flex-wrap gap-2">
                        {risk.riskFactors?.map((indicator, indicatorIndex) => (
                          <span key={indicatorIndex} className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs">
                            {indicator}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }) || (
                <div className="text-center py-8 text-gray-400">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No flight risk data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Row 2, Column 1: Goal Achievement Velocity */}
        <Card className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border-green-500/30 h-[500px] flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="text-white flex items-center gap-2">
              Goal Achievement Velocity
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="h-48 mb-4 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={goalVelocityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#444" opacity={0.6} />
                  <XAxis dataKey="category" stroke="#888" />
                  <YAxis domain={[0, 100]} stroke="#888" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #444',
                      borderRadius: '8px',
                      color: '#ffffff'
                    }}
                  />
                  <Bar dataKey="velocity" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="space-y-2">
                {teamMembersWithGoals.map((velocity, index) => (
                  <div key={index} className="bg-secondary/30 rounded-lg p-3 border border-green-500/20">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium">{velocity.employee}</span>
                        <div className="text-xs text-gray-400 mt-1">
                          Goal Type: <span className="text-green-400">{velocity.goalType}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`text-sm font-medium ${
                          velocity.velocityScore > 80 ? 'text-green-400' :
                          velocity.velocityScore > 60 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {velocity.velocityScore.toFixed(0)}% velocity
                        </span>
                        <span className="text-gray-400 text-sm">
                          {velocity.projectedCompletion}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Row 2, Column 2: Cross-Category Excellence Matrix */}
        <Card className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 border-purple-500/30 h-[500px] flex flex-col">
          <CardHeader className="flex-shrink-0">
            <CardTitle className="text-white flex items-center gap-2">
              Cross-Category Excellence Matrix
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto min-h-0">
            <div className="space-y-4">
              {predictiveData.crossCategoryExcellence?.map((excellence, index) => (
                <div key={index} className="bg-secondary/30 rounded-lg p-4 border border-purple-500/20">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="text-white font-semibold">{excellence.employee}</h4>
                      <p className="text-gray-300 text-sm">
                        Excellence Score: <span className="text-purple-400 font-medium">
                          {excellence.overallScore.toFixed(1)}
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        excellence.leadershipPotential > 80 ? 'bg-green-500/20 text-green-400' :
                        excellence.leadershipPotential > 60 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {excellence.leadershipPotential.toFixed(0)}% Potential
                      </span>
                    </div>
                  </div>

                  <div>
                    <p className="text-gray-300 text-sm mb-2">Strong Categories:</p>
                    <div className="flex flex-wrap gap-2">
                      {excellence.excellenceCategories?.map((category, categoryIndex) => (
                        <span key={categoryIndex} className="bg-purple-500/20 text-purple-400 px-2 py-1 rounded text-xs">
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )) || (
                <div className="text-center py-8 text-gray-400">
                  <Award className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No excellence data available</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PredictiveIntelligenceTab;
